import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import styles from './input-copy.module.scss';

import { Button } from 'views/components/UI/Button';
import { Input } from 'views/components/UI/Form-elements/Input';
import { CheckmarkRound, Copy } from 'views/components/UI/Icons';
import { Text } from 'views/components/UI/Text';

type CopyState = 'idle' | 'copying' | 'copied' | 'error';
const COPIED_STATE_MS = 2000;

interface IInputCopyProps {
  value: string;
  label?: string;
}

const InputCopy: FC<IInputCopyProps> = (props): ReactElement => {
  const { value, ...restProps } = props;
  const intl = useIntl();
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const requestVersion = useRef(0);

  useEffect(() => {
    requestVersion.current += 1;
    setCopyState('idle');
    return () => {
      requestVersion.current += 1;
    };
  }, [value]);

  useEffect(() => {
    if (copyState === 'copied') {
      const timeoutId = setTimeout(() => setCopyState('idle'), COPIED_STATE_MS);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [copyState]);

  async function onCopyClick(): Promise<void> {
    const version = requestVersion.current;
    setCopyState('copying');
    try {
      await navigator.clipboard.writeText(value);
      if (version === requestVersion.current) setCopyState('copied');
    } catch {
      if (version === requestVersion.current) setCopyState('error');
    }
  }

  return (
    <div className={styles['input-copy']}>
      <div className={styles['input-copy__control']}>
        <Input
          value={value}
          {...restProps}
          readOnly
          className={styles['input-copy__field']}
        />
        <Button
          type="button"
          mode="tertiary"
          className={styles['input-copy__button']}
          onClick={onCopyClick}
          disabled={copyState === 'copying'}
          aria-label={intl.formatMessage({ id: 'input-copy.action' })}
          aria-busy={copyState === 'copying'}
          icon={
            copyState === 'copied' ? (
              <CheckmarkRound size="16px" color="var(--sk-text-success)" />
            ) : (
              <Copy size="16px" color="currentColor" />
            )
          }
        />
      </div>
      <span role="status" className={styles['input-copy__status']}>
        {copyState === 'copied' && <FormattedMessage id="input-copy.copied" />}
      </span>
      {copyState === 'error' && (
        <div role="alert" className={styles['input-copy__error']}>
          <Text variant="bodySmall" color="var(--sk-text-danger)">
            <FormattedMessage id="input-copy.error" />
          </Text>
        </div>
      )}
    </div>
  );
};

export { InputCopy };
